'use client';

import React, { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { DOCUMENTS, type VerifiedDocument, type DocStatus } from '../documents';

const UI = {
  es: {
    title: 'Verificación de documentos',
    intro:
      'Cada documento oficial emitido por MERCALIA Connect Group LLC lleva un número de referencia único. Ingrese el código impreso en el documento (o escanee su código QR) para comprobar su autenticidad y vigencia.',
    placeholder: 'Ej.: CIS-MCG-2026-001',
    button: 'Verificar',
    notFoundTitle: 'Documento no encontrado',
    notFound:
      'El código ingresado no corresponde a ningún documento emitido por MERCALIA Connect Group LLC. Verifique el código o contáctenos en compliance@mercaliacg.com antes de confiar en el documento.',
    disclaimer:
      'Si usted recibió un documento a nombre de MERCALIA Connect Group LLC que no figura en este verificador, o cuyo estado no es "Vigente", no lo considere válido y repórtelo a compliance@mercaliacg.com.',
    fields: { ref: 'Referencia', type: 'Tipo de documento', date: 'Fecha de emisión', status: 'Estado', issuedTo: 'Emitido a', notes: 'Notas' },
    status: { valid: 'VIGENTE', superseded: 'REEMPLAZADO', revoked: 'REVOCADO', expired: 'VENCIDO' } as Record<DocStatus, string>,
    foundTitle: 'Documento verificado',
    issuer: 'Emitido por MERCALIA Connect Group LLC — Albuquerque, NM, EE. UU.',
    originalTitle: 'Documento original tal como fue emitido',
    originalIntro:
      'A continuación se muestra el documento oficial exactamente como fue emitido por MERCALIA Connect Group LLC. Compare el documento que usted recibió con esta versión: si difieren en cualquier punto (cifras, texto, sello o cualquier otro contenido), el documento recibido NO es válido y debe reportarlo a compliance@mercaliacg.com.',
    openOriginal: 'Abrir / descargar el documento original (PDF)',
    pdfFallback: 'Su navegador no puede mostrar el PDF incrustado. Use el botón para abrir el documento original.',
    corpTitle: 'Credenciales corporativas',
    corpIntro:
      '¿No tiene un código de documento? Estos son los registros oficiales de MERCALIA Connect Group LLC, verificables ante cada organismo emisor.',
    corpItems: [
      { label: 'Sociedad', value: 'MERCALIA Connect Group LLC — Nuevo México, Estados Unidos', note: 'Registro N.º 0008002716' },
      { label: 'D-U-N-S® Number', value: '149902101', note: 'Asignado por Dun & Bradstreet' },
      { label: 'Permiso de importación NOAA', value: 'IFTP00104977', note: 'Vigente hasta el 30 de julio de 2027' },
      { label: 'Beneficiario final', value: 'Declaración BOI presentada ante FinCEN', note: 'Departamento del Tesoro de los EE. UU.' },
      { label: 'Verificación de tercero', value: 'Empresa verificada por ConnectAmericas', note: 'Plataforma del Banco Interamericano de Desarrollo (BID) · 2 certificaciones vigentes: permiso internacional de comercio pesquero y constitución societaria' },
    ],
    corpFoot:
      'Para solicitar documentación societaria adicional, escríbanos a compliance@mercaliacg.com desde el correo corporativo de su empresa.',
  },
  en: {
    title: 'Document verification',
    intro:
      'Every official document issued by MERCALIA Connect Group LLC carries a unique reference number. Enter the code printed on the document (or scan its QR code) to check its authenticity and current status.',
    placeholder: 'E.g.: CIS-MCG-2026-001',
    button: 'Verify',
    notFoundTitle: 'Document not found',
    notFound:
      'The code entered does not match any document issued by MERCALIA Connect Group LLC. Please check the code or contact us at compliance@mercaliacg.com before relying on the document.',
    disclaimer:
      'If you received a document in the name of MERCALIA Connect Group LLC that does not appear in this verifier, or whose status is not "Valid", do not rely on it and report it to compliance@mercaliacg.com.',
    fields: { ref: 'Reference', type: 'Document type', date: 'Issue date', status: 'Status', issuedTo: 'Issued to', notes: 'Notes' },
    status: { valid: 'VALID', superseded: 'SUPERSEDED', revoked: 'REVOKED', expired: 'EXPIRED' } as Record<DocStatus, string>,
    foundTitle: 'Document verified',
    issuer: 'Issued by MERCALIA Connect Group LLC — Albuquerque, NM, USA.',
    originalTitle: 'Original document as issued',
    originalIntro:
      'Below is the official document exactly as issued by MERCALIA Connect Group LLC. Compare the document you received against this version: if they differ in any respect (figures, wording, seal or any other content), the document you received is NOT valid and should be reported to compliance@mercaliacg.com.',
    openOriginal: 'Open / download the original document (PDF)',
    pdfFallback: 'Your browser cannot display the embedded PDF. Use the button to open the original document.',
    corpTitle: 'Corporate credentials',
    corpIntro:
      'No document code? These are the official registrations of MERCALIA Connect Group LLC, verifiable with each issuing authority.',
    corpItems: [
      { label: 'Company', value: 'MERCALIA Connect Group LLC — New Mexico, United States', note: 'Registration No. 0008002716' },
      { label: 'D-U-N-S® Number', value: '149902101', note: 'Assigned by Dun & Bradstreet' },
      { label: 'NOAA import permit', value: 'IFTP00104977', note: 'Valid through 30 July 2027' },
      { label: 'Beneficial ownership', value: 'BOI report filed with FinCEN', note: 'U.S. Department of the Treasury' },
      { label: 'Third-party verification', value: 'Company verified by ConnectAmericas', note: 'Platform of the Inter-American Development Bank (IDB) · 2 certifications on file: international fisheries trade permit and certificate of incorporation' },
    ],
    corpFoot:
      'To request additional corporate documentation, write to compliance@mercaliacg.com from your company e-mail address.',
  },
};

// Bitácora de accesos (privada): avisa en segundo plano que se abrió/descargó un
// documento. Va siempre en try/catch y con sendBeacon para que NUNCA bloquee ni
// demore la página; si el registro falla, el visitante no se entera de nada.
function registrarAcceso(id: string, tipo: 'view' | 'download') {
  try {
    const u = `/api/verify-log?doc=${encodeURIComponent(id)}&tipo=${tipo}`;
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(u);
    } else {
      fetch(u, { method: 'POST', keepalive: true }).catch(() => {});
    }
  } catch {
    /* la bitácora nunca debe romper la verificación */
  }
}

const STATUS_STYLE: Record<DocStatus, string> = {
  valid: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  superseded: 'bg-amber-100 text-amber-800 border-amber-300',
  revoked: 'bg-red-100 text-red-800 border-red-300',
  expired: 'bg-gray-200 text-gray-700 border-gray-300',
};

function DocCard({ doc }: { doc: VerifiedDocument }) {
  const { lang } = useLanguage();
  const t = UI[lang as 'es' | 'en'] ?? UI.en;
  const l = (lang === 'es' ? 'es' : 'en') as 'es' | 'en';

  const rows: Array<[string, React.ReactNode]> = [
    [t.fields.ref, doc.ref],
    [t.fields.type, doc.type[l]],
    [t.fields.date, doc.issueDate],
    [
      t.fields.status,
      <span key="s" className={`inline-block px-3 py-0.5 rounded-full border text-sm font-bold tracking-wide ${STATUS_STYLE[doc.status]}`}>
        {t.status[doc.status]}
      </span>,
    ],
  ];
  if (doc.issuedTo) rows.push([t.fields.issuedTo, doc.issuedTo]);
  if (doc.notes) rows.push([t.fields.notes, doc.notes[l]]);

  return (
    <div className="bg-white border border-brand-border rounded-2xl p-8 shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 text-xl">✓</span>
        <h2 className="font-display text-2xl font-bold text-primary">{t.foundTitle}</h2>
      </div>
      <dl className="flex flex-col gap-4">
        {rows.map(([k, v]) => (
          <div key={k} className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-1 sm:gap-4">
            <dt className="text-sm font-bold text-primary">{k}</dt>
            <dd className="text-brand-muted">{v}</dd>
          </div>
        ))}
      </dl>
      <p className="text-sm text-brand-muted mt-8 pt-6 border-t border-brand-border">{t.issuer}</p>

      {doc.file && (
        <div className="mt-8 pt-6 border-t border-brand-border">
          <h3 className="font-display text-xl font-bold text-primary mb-3">{t.originalTitle}</h3>
          <p className="text-sm text-brand-muted leading-relaxed mb-4">{t.originalIntro}</p>
          <a
            href={doc.file}
            target="_blank"
            rel="noopener"
            onClick={() => registrarAcceso(doc.id, 'download')}
            className="inline-block rounded-xl bg-primary text-white font-bold px-6 py-3 mb-4 hover:bg-primary-light transition-colors"
          >
            {t.openOriginal}
          </a>
          <object
            data={doc.file}
            type="application/pdf"
            className="w-full rounded-xl border border-brand-border"
            style={{ height: '75vh', minHeight: 480 }}
          >
            <p className="text-sm text-brand-muted p-4">{t.pdfFallback}</p>
          </object>
        </div>
      )}
    </div>
  );
}

export function VerifyLookup() {
  const { lang } = useLanguage();
  const t = UI[lang as 'es' | 'en'] ?? UI.en;
  const [code, setCode] = useState('');
  const [notFound, setNotFound] = useState(false);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const id = code.trim().toUpperCase().replace(/[\s/]+/g, '-');
    if (DOCUMENTS[id]) {
      window.location.href = `/verify/${id}/`;
    } else {
      setNotFound(true);
    }
  };

  return (
    <section className="relative pt-36 pb-24 px-6">
      <div className="max-w-3xl mx-auto">
        <h1
          className="font-display font-bold text-primary mb-3"
          style={{ fontSize: 'clamp(36px, 5vw, 56px)', letterSpacing: '-0.03em', lineHeight: 1.05 }}
        >
          {t.title}
        </h1>
        <p className="text-brand-muted leading-relaxed mb-10">{t.intro}</p>

        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 mb-6">
          <input
            value={code}
            onChange={(e) => { setCode(e.target.value); setNotFound(false); }}
            placeholder={t.placeholder}
            className="flex-1 rounded-xl border border-brand-border bg-white px-5 py-3 text-primary outline-none focus:border-secondary"
          />
          <button
            type="submit"
            className="rounded-xl bg-primary text-white font-bold px-8 py-3 hover:bg-primary-light transition-colors"
          >
            {t.button}
          </button>
        </form>

        {notFound && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-6">
            <h2 className="font-display text-lg font-bold text-red-800 mb-2">{t.notFoundTitle}</h2>
            <p className="text-red-700 leading-relaxed">{t.notFound}</p>
          </div>
        )}

        <section className="bg-white border border-brand-border rounded-2xl p-6 sm:p-8 mb-8">
          <h2 className="font-display text-xl font-bold text-primary mb-2">{t.corpTitle}</h2>
          <p className="text-brand-muted leading-relaxed mb-6">{t.corpIntro}</p>

          <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
            {t.corpItems.map((item) => (
              <div key={item.label}>
                <dt className="text-xs font-bold uppercase tracking-wider text-brand-muted mb-1">
                  {item.label}
                </dt>
                <dd className="text-primary font-semibold leading-snug">{item.value}</dd>
                <dd className="text-sm text-brand-muted leading-snug mt-0.5">{item.note}</dd>
              </div>
            ))}
          </dl>

          <p className="text-sm text-brand-muted leading-relaxed mt-6 pt-5 border-t border-brand-border">
            {t.corpFoot}
          </p>
        </section>

        <p className="text-sm text-brand-muted leading-relaxed">{t.disclaimer}</p>
      </div>
    </section>
  );
}

export function VerifyResult({ id }: { id: string }) {
  const { lang } = useLanguage();
  const t = UI[lang as 'es' | 'en'] ?? UI.en;
  const doc = DOCUMENTS[id];
  const registrado = React.useRef(false);

  // Registra una apertura por carga de página, solo si el documento existe.
  React.useEffect(() => {
    if (doc && !registrado.current) {
      registrado.current = true;
      registrarAcceso(id, 'view');
    }
  }, [id, doc]);

  return (
    <section className="relative pt-36 pb-24 px-6">
      <div className="max-w-3xl mx-auto">
        <h1
          className="font-display font-bold text-primary mb-10"
          style={{ fontSize: 'clamp(32px, 4.5vw, 48px)', letterSpacing: '-0.03em', lineHeight: 1.05 }}
        >
          {t.title}
        </h1>
        {doc ? (
          <DocCard doc={doc} />
        ) : (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
            <h2 className="font-display text-lg font-bold text-red-800 mb-2">{t.notFoundTitle}</h2>
            <p className="text-red-700 leading-relaxed">{t.notFound}</p>
          </div>
        )}
        <p className="text-sm text-brand-muted leading-relaxed mt-8">{t.disclaimer}</p>
      </div>
    </section>
  );
}
